import { useMemo, useState } from "react";
import { cn } from "ui";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "icons/outline";
import {
  PaginationDefaultLimit,
  usePaginationParams,
} from "../hooks/usePaginationParams";

type PaginationProps = {
  total: number;
  onPageChange: (options: {
    params: { offset: number; limit: number };
    page: number;
    limit: number;
  }) => void;
  defaultLimit?: PaginationDefaultLimit;
};

export const Pagination: React.FC<PaginationProps> = ({
  total,
  onPageChange,
  defaultLimit,
}) => {
  const [params] = usePaginationParams(defaultLimit);
  const [currentLimit, setCurrentLimit] = useState<number>(
    Number(params.limit) || 10
  );

  const onChange = ({ page, limit }: { page?: number; limit?: number }) => {
    if (page || page === 0) {
      onPageChange({
        params: {
          offset: page * currentLimit,
          limit: currentLimit,
        },
        page: page + 1,
        limit: currentLimit,
      });

      return;
    }
    if (limit) {
      setCurrentLimit(limit);
      onPageChange({
        params: {
          offset: 0,
          limit,
        },
        page: 1,
        limit,
      });
    }
  };

  const pageIndex = Math.trunc(+params.offset / currentLimit);
  const pageCount = Math.ceil(total / currentLimit);

  const paginationRange = usePagination({
    currentPage: pageIndex + 1,
    pageSize: currentLimit,
    totalCount: total,
    siblingCount: 2,
  });

  return (
    <>
      {total > 0 && (
        <div className="mt-8 flex w-full flex-row justify-between pt-1 align-middle">
          <div className="flex flex-row items-center">
            <div className="flex items-center">
              <button
                className="mx-1.5 ml-0 inline-flex items-center justify-center rounded-lg border border-foundations-subtle p-1 text-center text-foundations-subtler hover:bg-foundations-subtlest hover:text-foundations-subtle hover:shadow focus:border-info-bold focus:outline-none focus:ring-1 focus:ring-info-bold disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => onChange({ page: 0 })}
                disabled={pageIndex === 0}
              >
                <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                className="mx-1.5 inline-flex items-center justify-center rounded-lg border border-foundations-subtle p-1 text-center text-foundations-subtler hover:bg-foundations-subtlest hover:text-foundations-subtle hover:shadow focus:border-info-bold  focus:outline-none focus:ring-1 focus:ring-info-bold disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => onChange({ page: pageIndex - 1 })}
                disabled={pageIndex === 0}
              >
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              {paginationRange.map((pageNumber, idx) => {
                if (pageNumber === usePagination.DOTS)
                  return (
                    <span
                      key={pageNumber + idx}
                      className="mx-1.5 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-center text-foundations-subtle"
                    >
                      &#8230;
                    </span>
                  );

                return (
                  <button
                    aria-current={
                      pageNumber === pageIndex + 1 ? "page" : undefined
                    }
                    key={pageNumber}
                    className={cn(
                      Number(pageNumber) === pageIndex + 1
                        ? "bg-info-subtler text-foundations-bold hover:border-info-subtle hover:text-foundations-boldest"
                        : "bg-transparent text-foundations-subtler hover:border-foundations-subtle hover:text-foundations-subtle",
                      "mx-1.5 inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg border border-transparent text-center hover:shadow focus:border-info-bold focus:text-info focus:shadow focus:outline-none focus:ring-1 focus:ring-info-bold"
                    )}
                    onClick={() => onChange({ page: +pageNumber - 1 })}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              <button
                className="mx-1.5 ml-0 inline-flex items-center justify-center rounded-lg border border-foundations-subtle p-1 text-center text-foundations-subtler hover:bg-foundations-subtlest hover:text-foundations-subtle hover:shadow focus:border-info-bold  focus:outline-none focus:ring-1 focus:ring-info-bold disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => onChange({ page: pageIndex + 1 })}
                disabled={pageIndex === pageCount - 1}
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
              <button
                className="mx-1.5 ml-0 inline-flex items-center justify-center rounded-lg border border-foundations-subtle p-1 text-center text-foundations-subtler hover:bg-foundations-subtlest hover:text-foundations-subtle hover:shadow focus:border-info-bold  focus:outline-none focus:ring-1 focus:ring-info-bold disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => onChange({ page: pageCount - 1 })}
                disabled={pageIndex === pageCount - 1}
              >
                <ChevronDoubleRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <select
            value={currentLimit}
            className="rounded-md border-0 border-transparent bg-foundations-default text-foundations-bold outline-none focus:ring-2 focus:ring-info-bold sm:max-w-xs sm:text-sm"
            onChange={(e) => {
              onChange({ limit: Number(e.target.value) });
            }}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize} items per page
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
};

// TODO: @Mo'men -> Add to DS with good refactor
const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

interface UsePaginationOptions {
  totalCount: number;
  pageSize: number;
  siblingCount?: number;
  currentPage: number;
}
export default function usePagination(options: UsePaginationOptions) {
  const { totalCount, pageSize, siblingCount = 1, currentPage } = options;

  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);

    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*usePagination.DOTS
    const totalPageNumbers = siblingCount + 5;

    /*
      Case 1:
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [1..totalPageCount]
    */
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    /*
    	Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
    */
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount
    );

    /*
      We do not show dots just when there is just one page number to be inserted between the extremes of sibling and the page limits i.e 1 and totalPageCount. Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPageCount - 2
    */
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    /*
    	Case 2: No left dots to show, but rights dots to be shown
    */
    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = range(1, leftItemCount);

      return [...leftRange, usePagination.DOTS, totalPageCount];
    }

    /*
    	Case 3: No right dots to show, but left dots to be shown
    */
    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = range(
        totalPageCount - rightItemCount + 1,
        totalPageCount
      );
      return [firstPageIndex, usePagination.DOTS, ...rightRange];
    }

    /*
    	Case 4: Both left and right dots to be shown
    */
    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [
        firstPageIndex,
        usePagination.DOTS,
        ...middleRange,
        usePagination.DOTS,
        lastPageIndex,
      ];
    }
    return [];
  }, [totalCount, pageSize, siblingCount, currentPage]);

  return paginationRange;
}

usePagination.DOTS = "DOTS";
